import {
  ASSISTANT_CHAT_STATUS_LABELS,
  assistantChartModel,
  type AssistantChartBlock,
  assistantTableNameKey,
  assistantTableTotalLine,
  formatAssistantCell,
  isAssistantNumericColumn,
  parseAssistantText,
  type AssistantCell,
  type AssistantChatStatus,
  type AssistantColumn,
  type AssistantFactsBlock,
  type AssistantShareBlock,
  type AssistantProposalBlock,
  type AssistantProposalView,
  type AssistantResultBlock,
  type AssistantTableBlock,
  type AssistantTextBlock,
  type AssistantTextSpan,
  type AssistantToolCall,
} from "@teamna/shared";
import { useEffect, useState } from "react";
import { Button, CopyMessageButton, DataTable, SegmentedControl, SparkIcon, type DataTableColumn } from "../../components/index.ts";
import { AssistantChartView } from "./assistant-chart.tsx";
import { formatDateTime } from "@teamna/web-client/lib/format.ts";

/**
 * The blocks of an AI Assistant answer (ai-assistant-v1.md, section 2 "Numbers"). The server built them
 * from the tool results, so this file only formats: it never counts, adds or rounds. The format
 * rules are in `@teamna/shared` (`assistant-format.ts`), the same for Desk and the mobile app.
 */

type Row = { id: string; cells: Record<string, AssistantCell> };

function cellView(value: AssistantCell, column: AssistantColumn, timezone: string) {
  const text = formatAssistantCell(value, column.kind, timezone, formatDateTime);
  return column.kind === "text" ? <span dir="auto">{text}</span> : text;
}

function tableColumns(block: AssistantTableBlock, timezone: string): DataTableColumn<Row>[] {
  const nameKey = assistantTableNameKey(block);
  return block.columns.map((column) => ({
    id: column.key,
    header: column.label,
    cell: (row: Row) => cellView(row.cells[column.key] ?? null, column, timezone),
    sortValue: (row: Row) => row.cells[column.key] ?? null,
    align: isAssistantNumericColumn(column.kind) ? "end" : "start",
    // On a phone: the name and the numbers. The other text columns go.
    hideOnPhone: column.kind === "text" && column.key !== nameKey,
  }));
}

export function AssistantTable({
  block,
  chart,
  timezone,
  onMore,
  moreBusy = false,
}: {
  block: AssistantTableBlock;
  /** The chart block right before the table: the card shows the chart first, with a switch to the table. */
  chart?: AssistantChartBlock;
  timezone: string;
  /** Shows the More button of the table, when it has one. */
  onMore?: () => void;
  moreBusy?: boolean;
}) {
  const rows: Row[] = block.rows.map((cells, index) => ({ id: String(index), cells }));
  const columns = tableColumns(block, timezone);
  const total = assistantTableTotalLine(block, timezone, formatDateTime);
  const model = chart ? assistantChartModel(chart, block) : null;
  const [view, setView] = useState<"chart" | "table">("chart");
  const showChart = model !== null && view === "chart";

  return (
    <section aria-label={block.title} className="mt-3">
      {block.headline ? (
        <p dir="auto" className="pb-2 text-[22px] leading-tight font-bold tabular-nums">
          {block.headline}
        </p>
      ) : null}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-[15px] font-semibold">{block.title}</h3>
          {block.caption ? <p className="pb-2 text-[13px] text-ink-2">{block.caption}</p> : null}
        </div>
        {model ? (
          <SegmentedControl
            label={`Chart or table: ${block.title}`}
            size="compact"
            value={view}
            onChange={setView}
            options={[
              { value: "chart", label: "Chart" },
              { value: "table", label: "Table" },
            ]}
          />
        ) : null}
      </div>
      {showChart ? (
        <div className="pt-1">
          <AssistantChartView model={model} timezone={timezone} />
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          density="compact"
          empty={{ title: "No rows", message: "Nothing matches in this period." }}
        />
      )}
      {total === null || model?.kind === "kpi" ? null : <p className="pt-2 text-[13px] font-semibold tabular-nums">{total}</p>}
      {block.truncated ? <p className="pt-1 text-[13px] text-ink-2">The table shows the first {block.rows.length} rows.</p> : null}
      {block.more && onMore ? (
        <div className="pt-2">
          <Button variant="secondary" size="sm" loading={moreBusy} onClick={onMore}>
            {block.more.label}
          </Button>
        </div>
      ) : null}
    </section>
  );
}

/** "3 tool calls", which opens the list of the tools and their inputs. */
export function AssistantToolCalls({ calls }: { calls: AssistantToolCall[] }) {
  const [open, setOpen] = useState(false);
  if (calls.length === 0) return null;
  return (
    <div className="mt-2">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="text-[13px] font-semibold text-accent-strong underline-offset-2 hover:underline"
      >
        {calls.length === 1 ? "1 tool call" : `${calls.length} tool calls`}
      </button>
      {open ? (
        <ul className="mt-1 space-y-1 rounded-lg border border-line bg-sunken/60 p-2 text-[12px] leading-5">
          {calls.map((call, index) => (
            // The calls of one turn never change order, and two can be the same call.
            // oxlint-disable-next-line react/no-array-index-key
            <li key={index} className="break-words">
              <span className="font-semibold">{call.name}</span>{" "}
              <span className="text-ink-2">{JSON.stringify(call.input)}</span>
              <span className={call.ok ? "text-ink-2" : "text-cancelled"}> · {call.summary}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Suggestions and plans
// ---------------------------------------------------------------------------

/** The colour of each chat status. The words are `ASSISTANT_CHAT_STATUS_LABELS`. */
const STATUS_CLASS: Record<AssistantChatStatus, string> = {
  waiting: "bg-late-surface text-late",
  new: "bg-tile-booked-tint text-tile-booked-ink",
  open: "bg-accent-surface text-accent-strong",
  done: "bg-confirmed-surface text-confirmed",
  snoozed: "border border-dashed border-line-strong text-quiet",
  closed: "border border-line-strong text-quiet",
};

/** The status of a chat, with its colour: amber waits for the person, violet is new, green is done. */
export function ChatStatusPill({ status }: { status: AssistantChatStatus }) {
  return (
    <span className={["inline-flex items-center rounded-pill px-2 py-0.5 text-[11px] font-semibold tracking-[0.02em] whitespace-nowrap", STATUS_CLASS[status]].join(" ")}>
      {ASSISTANT_CHAT_STATUS_LABELS[status]}
    </span>
  );
}

/** "What I found": the facts of a suggestion when Teamna made it. */
export function AssistantFacts({ block }: { block: AssistantFactsBlock }) {
  return (
    <section aria-label={block.title} className="mt-1 rounded-card border border-line bg-surface px-4 py-3">
      <h3 className="text-[14px] font-semibold">{block.title}</h3>
      <ul className="mt-1 list-disc space-y-0.5 pl-5 text-[14px] text-ink">
        {block.lines.map((line) => (
          <li key={line} dir="auto">
            {line}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * A message for the owner to share outside Teamna (Studio A1): the open spots message of Home (H2),
 * with the same Copy button. Teamna sends nothing, so it has no Confirm. `onCopied` runs after a
 * copy that worked (Desk sends `open_spots_shared` from it; Control Center reads only).
 */
export function AssistantShare({ block, onCopied }: { block: AssistantShareBlock; onCopied?: () => void }) {
  return (
    <section aria-label={block.title} className="mt-1 rounded-card border border-line bg-surface px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-[14px] font-semibold">{block.title}</h3>
        <CopyMessageButton text={block.text} label="Copy WhatsApp message" {...(onCopied ? { onCopied } : {})} />
      </div>
      <p className="mt-2 max-h-64 overflow-y-auto rounded-lg bg-sunken/60 px-3 py-2 text-[13.5px] whitespace-pre-line break-words text-ink" dir="auto">
        {block.text}
      </p>
    </section>
  );
}

/**
 * One plan. A plan on the table shows in full; one of several options has "Pick this". A plan that
 * is not on the table any more shows its title and what happened to it.
 */
export function AssistantProposal({
  block,
  view,
  picked = false,
  onPick,
}: {
  block: AssistantProposalBlock;
  /** From `assistantProposalView`. */
  view: AssistantProposalView;
  picked?: boolean;
  /** No `onPick`: read only (Control Center), so no "Pick this". */
  onPick?: () => void;
}) {
  const { live, statusText: status } = view;
  const options = onPick ? view.options : 1;
  return (
    <section
      aria-label={block.title}
      className={[
        "mt-2 rounded-card border px-4 py-3",
        live ? (picked ? "border-accent bg-accent-surface" : "border-line-strong bg-paper") : "border-line bg-surface text-ink-2",
      ].join(" ")}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          {block.version > 1 ? <p className="text-[12px] font-semibold text-ink-2">Plan {block.version}</p> : null}
          <h3 dir="auto" className={["text-[14.5px] font-semibold", live ? "text-ink" : ""].join(" ")}>
            {block.optionIndex !== null && options > 1 ? `${block.optionIndex + 1}. ` : ""}
            {block.title}
          </h3>
        </div>
        {live && options > 1 ? (
          <Button size="sm" variant={picked ? "primary" : "secondary"} readOnlySafe aria-pressed={picked} onClick={onPick}>
            {picked ? "Picked" : "Pick this"}
          </Button>
        ) : null}
      </div>
      {block.changed && live ? <p className="mt-1 text-[13px] text-accent-strong">{block.changed}</p> : null}
      {live ? (
        <>
          <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-[14px]">
            {block.lines.map((line) => (
              <li key={line} dir="auto">
                {line}
              </li>
            ))}
          </ul>
          {block.draft ? (
            <div className="mt-2 rounded-lg border border-line bg-surface px-3 py-2">
              <p className="text-[12px] font-semibold text-ink-2">The message</p>
              <p dir="auto" className="text-[14px] whitespace-pre-wrap">
                {block.draft}
              </p>
            </div>
          ) : null}
        </>
      ) : status ? (
        <p className="mt-0.5 text-[13px]">{status}</p>
      ) : null}
    </section>
  );
}

function Spans({ spans }: { spans: AssistantTextSpan[] }) {
  return spans.map((span, index) => {
    const key = `${index}`;
    if (span.bold && span.italic) {
      return (
        <strong key={key} className="font-semibold">
          <em>{span.text}</em>
        </strong>
      );
    }
    if (span.bold) {
      return (
        <strong key={key} className="font-semibold">
          {span.text}
        </strong>
      );
    }
    return span.italic ? <em key={key}>{span.text}</em> : span.text;
  });
}

type TextTableRow = { id: string; cells: AssistantTextSpan[][] };

type TextTableBlock = Extract<AssistantTextBlock, { type: "table" }>;

function textTableColumns(block: TextTableBlock): DataTableColumn<TextTableRow>[] {
  return block.header.map((header, column) => ({
    id: String(column),
    header,
    cell: (row: TextTableRow) => (
      <span dir="auto">
        <Spans spans={row.cells[column] ?? []} />
      </span>
    ),
    align: block.align[column] ?? "start",
  }));
}

/**
 * A table that the model wrote in its text. The shared table draws it, with every column also on a
 * phone (the table scrolls sideways): the model chose the columns, so none is less important.
 */
function TextTable({ block }: { block: TextTableBlock }) {
  const rows: TextTableRow[] = block.rows.map((cells, index) => ({ id: String(index), cells }));
  return (
    <DataTable
      columns={textTableColumns(block)}
      rows={rows}
      rowKey={(row) => row.id}
      density="compact"
      empty={{ title: "No rows", message: "The table has no rows." }}
    />
  );
}

/**
 * The text of an answer: paragraphs, lists, tables and bold from the small Markdown set of
 * `parseAssistantText`. React draws each part as text, so a name in the data cannot add markup.
 */
export function AssistantText({ text, error = false }: { text: string; error?: boolean }) {
  const blocks = parseAssistantText(text);
  return (
    <div dir="auto" className={["space-y-2 text-[15px] leading-[1.5] break-words", error ? "text-cancelled-ink" : "text-ink"].join(" ")}>
      {blocks.map((block, index) => {
        // The blocks of one answer never change order.
        const key = `${block.type}-${index}`;
        if (block.type === "paragraph") {
          return (
            <p key={key} className="whitespace-pre-wrap">
              <Spans spans={block.spans} />
            </p>
          );
        }
        if (block.type === "table") return <TextTable key={key} block={block} />;
        const List = block.ordered ? "ol" : "ul";
        return (
          <List
            key={key}
            start={block.ordered && block.start !== 1 ? block.start : undefined}
            className={["space-y-1 ps-5", block.ordered ? "list-decimal" : "list-disc"].join(" ")}
          >
            {block.items.map((spans, item) => (
              // oxlint-disable-next-line react/no-array-index-key
              <li key={item} className="ps-0.5">
                <Spans spans={spans} />
              </li>
            ))}
          </List>
        );
      })}
    </div>
  );
}

/** The time after which "working" adds that a long question takes longer. */
const SLOW_ANSWER_MS = 12_000;

/** AI Assistant works on the answer: three dots that rise in turn, and after a while a second line. */
export function AssistantWorking() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), SLOW_ANSWER_MS);
    return () => clearTimeout(timer);
  }, []);
  return (
    <output aria-live="polite" className="flex items-start gap-2.5">
      <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-accent-surface text-accent-strong">
        <SparkIcon size={16} />
      </span>
      <span className="min-w-0">
        <span className="flex items-center gap-2 pt-1.5">
          <span aria-hidden="true" className="assistant-dots inline-flex items-center gap-1">
            <span />
            <span />
            <span />
          </span>
          <span className="text-[14px] text-ink-2">AI Assistant is working on it</span>
        </span>
        {slow ? <span className="block pt-1 text-[13px] text-ink-3">It reads your data. Some questions take longer.</span> : null}
      </span>
    </output>
  );
}

/** A question of the person, on the right side of the chat. */
export function UserBubble({ text }: { text: string }) {
  return (
    <div className="flex justify-end">
      <div dir="auto" className="bubble bubble-mine max-w-[85%] rounded-card rounded-br-md px-3.5 py-2.5 text-[15px] leading-[1.45] whitespace-pre-wrap break-words">
        {text}
      </div>
    </div>
  );
}

/** What Teamna did after Confirm, or why it did not. */
export function AssistantResult({ block }: { block: AssistantResultBlock }) {
  return (
    <p
      dir="auto"
      className={[
        "mt-1 rounded-card px-4 py-2.5 text-[14px]",
        block.ok ? "bg-confirmed-surface text-confirmed-ink" : "bg-cancelled-surface text-cancelled-ink",
      ].join(" ")}
    >
      {block.text}
    </p>
  );
}
